import { createContext, PropsWithChildren, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import ErrorAlert from '../components/ErrorAlert'
import LoadingScreen from '../components/LoadingScreen'
import { ZodError, ZodType } from 'zod'
import { formatZodError } from '../utils/config-validation-error'

export interface ConfigProviderTranslations {
  validationError: (formattedError: string) => string
  unknownError: () => string
}

export interface CreateConfigProviderOptions<T> {
  configSchema: ZodType<T>
  getConfig: () => Promise<unknown>
  onConfigValidated: (config: T) => void
  translations: ConfigProviderTranslations
}

export function createConfigProvider<T>({
  configSchema,
  getConfig,
  onConfigValidated,
  translations
}: CreateConfigProviderOptions<T>) {
  const ConfigContext = createContext<T | null>(null)

  const useConfig = () => {
    const context = useContext<T | null>(ConfigContext)
    if (!context) {
      throw new Error('useConfigContext must be used within a ConfigProvider')
    }
    return context
  }

  function ConfigProvider({ children }: Readonly<PropsWithChildren>) {
    const testIdInit = 'config'

    const {
      data: config,
      isPending,
      isError,
      error: fetchError
    } = useQuery({
      queryKey: ['config'],
      queryFn: getConfig,
      refetchOnReconnect: true,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    })

    let validationError = null
    let validatedConfig = null

    if (config && !isError) {
      try {
        validatedConfig = configSchema.parse(config)
        onConfigValidated(validatedConfig)
      } catch (e: unknown) {
        if (e instanceof ZodError) {
          validationError = translations.validationError(formatZodError(e))
        } else {
          validationError = translations.unknownError()
        }
      }
    }

    const displayError = isError ? fetchError.message : validationError

    if (isPending) {
      return <LoadingScreen data-testid={`${testIdInit}-loading`} />
    }

    if (displayError) {
      return (
        <ErrorAlert data-testid={`${testIdInit}-error`}>
          {displayError}
        </ErrorAlert>
      )
    }

    return (
      <ConfigContext.Provider value={validatedConfig}>
        {children}
      </ConfigContext.Provider>
    )
  }

  return { ConfigProvider, useConfig }
}
